/**
 * <s-inview-video> — plays a muted, looping product video only while it is on screen.
 *
 * Wraps a native <video muted loop playsinline preload="none">. An IntersectionObserver
 * starts playback when at least half the video is visible and pauses it when it leaves,
 * so a row of videos never downloads or decodes off-screen. Visitors who prefer reduced
 * motion get no autoplay; the video shows native controls so they can play it themselves.
 *
 * Expected DOM:
 *   <s-inview-video><video muted loop playsinline preload="none" poster="…">…</video></s-inview-video>
 */
class SInviewVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    if (!this.video) return;

    this.video.muted = true;
    this.video.playsInline = true;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.video.controls = true;
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.video.play().catch(() => {
              this.video.controls = true;
            });
          } else {
            this.video.pause();
          }
        }
      },
      { threshold: 0.5 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get("s-inview-video")) {
  customElements.define("s-inview-video", SInviewVideo);
}
